import React, { useCallback, useEffect, useContext, useRef, useState } from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Modal from 'react-modal';

import { faIdCard, faCheckCircle, faTimesCircle } from '@fortawesome/free-solid-svg-icons';

import PersonaLib from 'persona';

/** context */
import { UserContext } from '../../context/UserContext';
import { useRudder } from '../../hooks/rudder';

/** service */
import { updateApplication, updateIdValidateInquiryId, validateProperty } from '../../service/api';

const CustomPersonaLink = ({
  handleContinue,
  setLoading,
  options,
  feature,
  handleSkip,
  setTitle,
  setModalIsOpen,
}) => {
  const embeddedClientRef = useRef(null);
  const { updateAppData } = useContext(UserContext);
  const { errorEvent } = useRudder();
  const stopResumeOnStatus = () => {
    const stopResumeOnStatusArr = ['completed', 'failed', 'approved', 'declined', 'needs_review'];
    return !stopResumeOnStatusArr.includes(feature.value[0]?.status.toLowerCase());
  };
  const updateAppWithData = useCallback(
    async (data, properties) => {
      setLoading(true);
      try {
        const res = await updateApplication([
          {
            itemType: feature.itemType,
            dataType: feature.dataType,
            name: feature.name,
            value: data,
          },
        ]);

        await validateProperty(feature.name);
        if (res && res.data) {
          updateAppData(res.data);
        }
      } catch (e) {
        console.log(e);
        errorEvent('Error API', e);
      }

      setLoading(false);
    },
    [updateAppData, setLoading]
  );

  useEffect(() => {
    async function init() {
      const sessionToken = await updateIdValidateInquiryId();
      await updateAppWithData([{ ...options[0], sessionToken: sessionToken.data }], 'sessionToken');
      embeddedClientRef.current = new PersonaLib.Client({
        templateVersionId: '',
        host: 'production',
        inquiryId: options[0].inquiryId,
        // We are not using templateId in case we have inquiryId
        templateId: options[0].inquiryId ? undefined : options[0].templateId,
        referenceId: options[0].referenceId,
        sessionToken: sessionToken.data,
        environment: process.env.REACT_APP_PERSONA_ENVIRONMENT || 'production',
        onComplete: ({ inquiryId, status }) => {
          if (status === 'completed') {
            updateAppWithData([{ ...options[0], status: 'COMPLETED' }], 'status');
          } else if (status === 'approved') {
            updateAppWithData([{ ...options[0], status: 'APPROVED' }], 'status');
          } else {
            updateAppWithData([{ ...options[0], status: 'FAILED' }], 'status');
          }
        },
        onError: (error) => {
          updateAppWithData([{ ...options[0], status: 'FAILED' }], 'status');
        },
        onEvent: (name, meta) => {
          switch (name) {
            case 'start': {
              updateAppWithData([{ ...options[0], status: 'CREATED' }], 'status');
              break;
            }
            case 'exit': {
              updateAppWithData([{ ...options[0], status: 'CREATED' }], 'status');
              break;
            }
          }
        },
      });
    }
    if (stopResumeOnStatus()) {
      init();
    }
    // eslint-disable-next-line
  }, []);

  const openClient = useCallback(() => {
    embeddedClientRef.current?.open();
    // eslint-disable-next-line
  }, [embeddedClientRef.current]);

  const option0 = options[0];

  const generateMainTitle = () => {
    const baseTitle = feature?.displayProperties?.meta.TITLE ?? 'We need to verify';
    return option0?.status === 'COMPLETED' || option0?.status === 'APPROVED'
      ? 'We successfully verified it’s you!'
      : baseTitle;
  };

  const renderTitle = useCallback(() => {
    if (option0.status === 'COMPLETED') {
      return 'Verify Again';
    }

    if (option0.status === 'FAILED') {
      return 'Resume verification';
    }

    return 'Begin Verification with Persona';
  }, [option0]);

  const renderMessage = useCallback(() => {
    if (option0.inquiryId) {
      if (option0.status === 'COMPLETED' || option0.status === 'APPROVED') {
        return '';
      }

      if (option0.status === 'FAILED' || option0.status === 'CLOSED') {
        return 'We are sorry but we could not verify your ID at this time after multiple attempts.';
      }

      return (
        feature?.displayProperties?.meta?.SUBTITLE ?? (
          <div>
            You will need to verify your identity by uploading a picture of your valid
            government-issued ID and taking a selfie using a device with a front-facing camera using{' '}
            <a href="https://withpersona.com/security" target="_blank" rel="noreferrer">
              Persona
            </a>
          </div>
        )
      );
    }

    return undefined;
    // eslint-disable-next-line
  }, [option0]);

  const renderIcon = useCallback(() => {
    if (option0.status === 'COMPLETED' || option0.status === 'APPROVED') {
      return <FontAwesomeIcon color="green" icon={faCheckCircle} />;
    }

    if (option0.status === 'FAILED') {
      return <FontAwesomeIcon color="red" icon={faTimesCircle} />;
    }

    return <FontAwesomeIcon color="#631fff" icon={faIdCard} />;
  }, [option0]);

  setTitle(generateMainTitle());

  const renderButton = () => {
    if (
      option0.status !== 'COMPLETED' &&
      option0.status !== 'FAILED' &&
      option0.status !== 'CLOSED' &&
      option0.status !== 'APPROVED'
    ) {
      return (
        <>
          <button className="persona-btn" onClick={openClient}>
            {renderTitle()}
          </button>
          {!feature.required && (
            <button className="persona-btn" onClick={handleSkip}>
              Skip
            </button>
          )}
        </>
      );
    }
  };

  return (
    <section>
      <h3 className="icon">{renderIcon()}</h3>
      <h4 className="subtitle">{renderMessage()}</h4>
      <h4 className="subtitle">
        <a href="#" onClick={() => setModalIsOpen(true)}>
          Learn more about Persona
        </a>
      </h4>
      {renderButton()}
    </section>
  );
};

const IdVerification = ({ handleContinue, feature }) => {
  const { setLoading } = useContext(UserContext);
  const [title, setTitle] = useState('');
  const [modalIsOpen, setModalIsOpen] = useState(false);

  const handleSkip = async () => {
    await validateProperty(feature.name);
    handleContinue();
  };

  return (
    <div className="documents-information">
      <h1 className="title">{title}</h1>
      <div className="persona-options">
        <div className="option-item">
          {feature?.value[0].templateId && (
            <>
              <CustomPersonaLink
                options={feature.value}
                handleContinue={handleContinue}
                setLoading={setLoading}
                handleSkip={handleSkip}
                feature={feature}
                setTitle={setTitle}
                setModalIsOpen={setModalIsOpen}
              />
            </>
          )}
        </div>
      </div>
      <Modal isOpen={modalIsOpen} className="field-modal" contentLabel="bla blabla">
        <span onClick={() => setModalIsOpen(false)}>x</span>
        <div>
          <a href="https://withpersona.com/security" target="_blank" rel="noreferrer">
            Persona
          </a>{' '}
          is a third-party platform utilized by Lendistry in its fraud prevention and mitigation
          process. Persona enables Lendistry to verify an individual's identity and protect against
          identity spoofing by automatically comparing the individual's selfie to their ID portrait
          with a 3-point composite and biometric liveness checks.
        </div>
        <div className="btn-line">
          <button onClick={() => setModalIsOpen(false)} className="btn">
            Close
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default IdVerification;
